/**
 * useResolvedUserName Hook
 *
 * Resolves user IDs to human-readable display names.
 *
 * SECURITY UPDATE (Audit Jan 2026):
 * Cross-user profile resolution now goes through the resolveUserProfiles Cloud Function.
 * This hook uses the secure resolution service which validates team membership.
 *
 * Resolution order:
 * 1. Secure Cloud Function (validates team membership)
 * 2. Team member data (fallback for same-team users)
 * 3. Email prefix or "Gebruiker" (final fallback)
 *
 * For new code, prefer using:
 * - useSecureUserProfile(uid) for single user
 * - useSecureUserProfiles(uids) for batch resolution
 */

import { useMemo } from 'react';
import { useTeamMembers, TeamMember } from './useTeamMembers';
import { useSecureUserProfile, useSecureUserProfiles } from './useSecureUserProfile';

// =============================================================================
// Types
// =============================================================================

interface UserProfileData {
  firstName?: string;
  lastName?: string;
  fullName?: string;
}

interface ResolvedName {
  displayName: string;
  firstName?: string;
  lastName?: string;
  source: 'profile' | 'user' | 'member' | 'email' | 'fallback';
}

// =============================================================================
// Query Keys (kept for backward compatibility)
// =============================================================================

export const userNameKeys = {
  all: ['userName'] as const,
  profile: (userId: string) => [...userNameKeys.all, 'profile', userId] as const,
};

/**
 * Build display name from profile data
 */
function buildDisplayName(profile: UserProfileData | null): string | null {
  if (!profile) return null;

  // Priority 1: fullName
  if (profile.fullName) {
    return profile.fullName;
  }

  // Priority 2: firstName + lastName
  if (profile.firstName || profile.lastName) {
    return [profile.firstName, profile.lastName].filter(Boolean).join(' ');
  }

  return null;
}

// =============================================================================
// Hooks
// =============================================================================

/**
 * Hook to resolve a single user's display name
 *
 * SECURITY UPDATE (Audit Jan 2026):
 * Now uses the secure Cloud Function for cross-user profile resolution.
 * Falls back to team member data for same-team users.
 *
 * @param userId - The user ID to resolve
 * @returns Resolved name with source information
 */
export function useResolvedUserName(userId: string | null | undefined): ResolvedName {
  const { data: members = [] } = useTeamMembers();

  // Use secure Cloud Function for profile resolution
  const { displayName: secureDisplayName, isLoading } = useSecureUserProfile(userId);

  return useMemo((): ResolvedName => {
    if (!userId) {
      return { displayName: 'Gebruiker', source: 'fallback' };
    }

    // Priority 1: Secure Cloud Function result
    if (secureDisplayName) {
      const parts = secureDisplayName.split(' ');
      return {
        displayName: secureDisplayName,
        firstName: parts[0],
        lastName: parts.length > 1 ? parts.slice(1).join(' ') : undefined,
        source: 'profile',
      };
    }

    // Priority 2: Team member displayName (fallback for same-team users)
    const member = members.find((m) => m.uid === userId);
    if (member?.displayName) {
      return {
        displayName: member.displayName,
        firstName: member.firstName ?? member.displayName.split(' ')[0],
        lastName: member.lastName,
        source: 'member',
      };
    }

    // Priority 3: Member firstName + lastName
    if (member?.firstName || member?.lastName) {
      const name = [member.firstName, member.lastName].filter(Boolean).join(' ');
      return {
        displayName: name,
        firstName: member.firstName,
        lastName: member.lastName,
        source: 'member',
      };
    }

    // Priority 4: Email prefix
    if (member?.email) {
      const emailPrefix = member.email.split('@')[0] ?? 'Gebruiker';
      return {
        displayName: emailPrefix,
        source: 'email',
      };
    }

    // Show loading indicator while fetching
    if (isLoading) {
      return { displayName: '...', source: 'fallback' };
    }

    // Final fallback
    return { displayName: 'Gebruiker', source: 'fallback' };
  }, [userId, secureDisplayName, isLoading, members]);
}

/**
 * Hook to get just the first name for greetings
 *
 * SECURITY UPDATE (Audit Jan 2026):
 * Now uses the secure Cloud Function for profile resolution.
 *
 * @param userId - The user ID to resolve
 * @returns First name or fallback
 */
export function useResolvedFirstName(userId: string | null | undefined): string {
  const { data: members = [] } = useTeamMembers();

  // Use secure Cloud Function for profile resolution
  const { displayName: secureDisplayName } = useSecureUserProfile(userId);

  return useMemo((): string => {
    if (!userId) return '';

    // Priority 1: Secure Cloud Function result - extract first name
    if (secureDisplayName) {
      return secureDisplayName.split(' ')[0] ?? '';
    }

    // Priority 2: Team member data
    const member = members.find((m) => m.uid === userId);
    if (member?.firstName) return member.firstName;
    if (member?.displayName) return member.displayName.split(' ')[0] ?? '';

    return '';
  }, [userId, secureDisplayName, members]);
}

/**
 * Hook to get a map of user IDs to resolved names
 * Useful when displaying multiple users efficiently
 *
 * SECURITY UPDATE (Audit Jan 2026):
 * Now uses the secure Cloud Function for batch profile resolution.
 *
 * @param userIds - Array of user IDs to resolve
 * @returns Map of userId -> displayName
 */
export function useResolvedUserNameMap(userIds: string[]): Record<string, string> {
  const { data: members = [] } = useTeamMembers();

  // Use secure Cloud Function for batch profile resolution
  const { profiles: secureProfiles } = useSecureUserProfiles(userIds);

  return useMemo((): Record<string, string> => {
    const map: Record<string, string> = {};

    for (const userId of userIds) {
      if (!userId) continue;

      // Priority 1: Secure Cloud Function result
      const secureName = secureProfiles[userId];
      if (secureName) {
        map[userId] = secureName;
        continue;
      }

      // Priority 2: Team member data
      const member = members.find((m) => m.uid === userId);
      if (member?.displayName) {
        map[userId] = member.displayName;
        continue;
      }
      if (member?.firstName || member?.lastName) {
        map[userId] = [member.firstName, member.lastName].filter(Boolean).join(' ');
        continue;
      }
      if (member?.email) {
        map[userId] = member.email.split('@')[0] ?? 'Gebruiker';
        continue;
      }

      // Fallback
      map[userId] = 'Gebruiker';
    }

    return map;
  }, [userIds, members, secureProfiles]);
}

// =============================================================================
// Utility Functions (for non-hook contexts)
// =============================================================================

/**
 * Resolve a user's display name given team members data
 * Use this in components that already have team members loaded
 *
 * @param userId - The user ID to resolve
 * @param members - Array of team members
 * @param profileData - Optional profile data if already fetched
 * @returns Display name string
 */
export function resolveUserName(
  userId: string | null | undefined,
  members: TeamMember[],
  profileData?: UserProfileData | null
): string {
  if (!userId) return 'Gebruiker';

  // Priority 1: Profile data
  const profileName = buildDisplayName(profileData ?? null);
  if (profileName) return profileName;

  // Priority 2: Team member data
  const member = members.find((m) => m.uid === userId);
  if (member?.displayName) return member.displayName;
  if (member?.firstName || member?.lastName) {
    return [member.firstName, member.lastName].filter(Boolean).join(' ');
  }
  if (member?.email) return member.email.split('@')[0] ?? 'Gebruiker';

  return 'Gebruiker';
}
